import { forwardRef, useEffect, useRef, useState } from 'react'

const VARIANT_CLASSES = {
    primary: 'bg-accent text-black border-black dark:border-white hover:bg-accent-hover',
    secondary: 'bg-white dark:bg-zinc-950 text-black dark:text-white border-black dark:border-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black',
    ghost: 'bg-transparent text-black dark:text-white border-transparent hover:border-black dark:hover:border-white shadow-none hover:shadow-brutal-sm',
    destructive: 'bg-white dark:bg-zinc-950 text-red-600 border-red-600 hover:bg-red-600 hover:text-white',
}

/**
 * Brutalist button primitive. Compresses into its own offset shadow on
 * press/active. Destructive actions with `confirm` require a second click
 * within 3s ("arm" state) instead of a native confirm() dialog.
 */
const Button = forwardRef(function Button(
    {
        variant = 'primary',
        as: Component = 'button',
        confirm = false,
        confirmLabel = 'Click to confirm',
        className = '',
        children,
        onClick,
        disabled,
        ...props
    },
    ref
) {
    const [armed, setArmed] = useState(false)
    const timeoutRef = useRef(null)

    useEffect(() => () => clearTimeout(timeoutRef.current), [])

    const handleClick = (e) => {
        if (confirm) {
            if (!armed) {
                setArmed(true)
                timeoutRef.current = setTimeout(() => setArmed(false), 3000)
                return
            }
            clearTimeout(timeoutRef.current)
            setArmed(false)
        }
        onClick?.(e)
    }

    return (
        <Component
            ref={ref}
            onClick={handleClick}
            disabled={disabled}
            className={[
                'inline-flex items-center justify-center gap-1.5 rounded-none border-2 px-4 py-2 font-mono text-sm font-semibold uppercase tracking-wide',
                'shadow-brutal transition-[transform,box-shadow,background-color,color,border-color] duration-100 ease-out',
                'hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-brutal-hover',
                'active:translate-x-1 active:translate-y-1 active:shadow-none',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
                armed
                    ? 'bg-red-600 text-white border-red-600 animate-pulse'
                    : VARIANT_CLASSES[variant],
                className,
            ].join(' ')}
            {...props}
        >
            {armed ? confirmLabel : children}
        </Component>
    )
})

export default Button
